import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ShieldCheck, Eye, FileDown, Users as UsersIcon, Activity,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from "recharts";

const ANALYTICS_RANGES = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

export default function AnalyticsTab({ roomId }) {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/rooms/${roomId}/analytics`, { params: { days } }).then((r) => setData(r.data));
    api.get(`/rooms/${roomId}/activity`).then((r) => setActivity(r.data));
  }, [roomId, days]);

  if (!data) return <div className="p-8 label-mono text-muted-foreground">— Loading analytics —</div>;

  const tiles = [
    { label: "Sign-ins",        value: data.totals.signins,    icon: ShieldCheck, tone: "text-emerald-500 bg-emerald-500/10" },
    { label: "Document views",  value: data.totals.views,      icon: Eye,         tone: "text-sky-500 bg-sky-500/10" },
    { label: "Downloads",       value: data.totals.downloads,  icon: FileDown,    tone: "text-amber-500 bg-amber-500/10" },
    { label: "Members active",  value: `${data.totals.active_members}/${data.totals.members}`,
      icon: UsersIcon, tone: "text-indigo-500 bg-indigo-500/10" },
  ];

  // Truncate long doc names for the bar chart's X-axis without losing the
  // full title in the tooltip.
  const topDocs = (data.per_document || []).slice(0, 8).map((d) => ({
    ...d,
    shortName: d.name && d.name.length > 18 ? `${d.name.slice(0, 17)}…` : d.name,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono text-muted-foreground">— Engagement</div>
          <h2 className="font-display text-2xl tracking-tight mt-1">Room analytics</h2>
        </div>
        <div className="flex border border-border rounded-md overflow-hidden">
          {ANALYTICS_RANGES.map((r) => (
            <button
              key={r.days}
              data-testid={`analytics-range-${r.label}`}
              onClick={() => setDays(r.days)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider transition ${
                days === r.days ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
            >Last {r.label}</button>
          ))}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="border border-border bg-card rounded-md p-4 flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${t.tone}`}>
              <t.icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-2xl tracking-tight truncate">{t.value}</div>
              <div className="label-mono text-muted-foreground truncate">{t.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="border border-border bg-card rounded-md p-5 overflow-hidden">
          <div className="label-mono text-muted-foreground mb-4">— Activity over time</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} width={36} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
              <Line type="monotone" dataKey="views" name="Views" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="signins" name="Sign-ins" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="downloads" name="Downloads" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border bg-card rounded-md p-5 overflow-hidden">
          <div className="label-mono text-muted-foreground mb-4">— Top documents by views</div>
          {topDocs.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">
              No document views in this range.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topDocs} margin={{ top: 8, right: 16, left: 0, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="shortName" stroke="hsl(var(--muted-foreground))"
                  fontSize={10} interval={0} angle={-30} dy={12} height={60}
                />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} width={36} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6 }}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""}
                />
                <Bar dataKey="views" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Participant engagement */}
      <div className="border border-border bg-card rounded-md overflow-hidden">
        <div className="label-mono text-muted-foreground px-4 py-3 border-b border-border">— Participant engagement</div>
        <div className="overflow-x-auto">
          <div className="grid grid-cols-[minmax(220px,1fr)_120px_110px_110px_120px] label-mono text-muted-foreground border-b border-border bg-secondary/60 min-w-[680px]">
            <div className="px-4 py-2.5">User</div>
            <div className="px-4 py-2.5">Role</div>
            <div className="px-4 py-2.5 text-right">Sign-ins</div>
            <div className="px-4 py-2.5 text-right">Views</div>
            <div className="px-4 py-2.5 text-right">Downloads</div>
          </div>
          {data.per_user.length === 0 && (
            <div className="px-4 py-10 text-sm text-muted-foreground text-center">No participant activity in this range.</div>
          )}
          {data.per_user.map((u) => (
            <div key={u.user_id} className="grid grid-cols-[minmax(220px,1fr)_120px_110px_110px_120px] border-b border-border last:border-b-0 min-w-[680px] hover:bg-secondary/30 transition">
              <div className="px-4 py-3 text-sm flex items-center gap-2 min-w-0">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${u.active ? "bg-emerald-500" : "bg-muted-foreground/30"}`} />
                <span className="truncate">{u.email}</span>
              </div>
              <div className="px-4 py-3 mono text-xs uppercase tracking-wider">
                <span className={`px-2 py-0.5 rounded ${u.role === "admin" ? "bg-amber-500/15 text-amber-600" : "bg-secondary"}`}>{u.role}</span>
              </div>
              <div className="px-4 py-3 mono text-xs text-right">{u.signins}</div>
              <div className="px-4 py-3 mono text-xs text-right">{u.views}</div>
              <div className="px-4 py-3 mono text-xs text-right">{u.downloads}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Activity feed */}
      <div className="border border-border bg-card rounded-md overflow-hidden">
        <div className="label-mono text-muted-foreground px-4 py-3 border-b border-border flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Activity feed
        </div>
        <div className="max-h-80 overflow-auto divide-y divide-border">
          {activity.length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground">No activity yet.</div>}
          {activity.map((a) => (
            <div key={a.id} className="px-4 py-3 text-sm flex items-center justify-between gap-3">
              <div className="min-w-0 truncate">
                <span className="font-medium">{a.user_name || a.user_email}</span>
                <span className="text-muted-foreground"> — {a.action.replace(/_/g, " ")}</span>
              </div>
              <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">{new Date(a.ts).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
